import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import * as yup from "yup";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { useUpdateGpInternalEstimatesForDealOpportunity } from "@vega-api/generated/coinvest-deal-opportunity-gp-actions/coinvest-deal-opportunity-gp-actions";
import {
  getGetDealShortlistQueryKey,
  useUpdateDealShortlistItem,
} from "@vega-api/generated/coinvest-deal-shortlist/coinvest-deal-shortlist";
import { getGetDealTrackingForGpQueryKey } from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking";
import { type Currency as CurrencyType } from "@vega-api/generated/types/currency";
import { MoneyField } from "@vega-core/components/Form/Fields/MoneyField";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { useToast } from "@vega-core/components/Toast/useToast";
import { type CoreMoney } from "@vega-core/models/money";
import { m } from "@vega-core/paraglide/messages";
import { useDefaultCurrency } from "@vega-core/utils/formatMoney";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

import { toMoneyPayload } from "../utils/moneyUtils";

import { ModalButtonGroup } from "./ModalButtonGroup";

type EditEstimatedMinTicketSizeModalProps = {
  dealOpportunityId?: string;
  dealShortlistItemId?: string;
  dealId: string;
  dealCurrency?: CurrencyType;
  isOpen: boolean;
  onClose: () => void;
  initialValues?: {
    estimatedTicketSizeMin?: CoreMoney;
    estimatedTicketSizeMax?: CoreMoney;
    gpIndicativeOrder?: CoreMoney;
  };
};

type FormValues = {
  amount: CoreMoney;
};

const createValidationSchema = (maximumAmount?: number) =>
  yup.object({
    amount: yup.object({
      amount: yup
        .number()
        .required(m.coInvestments_gpEstimatedTicketSize_modal_minRequired())
        .min(
          0,
          m.coInvestments_gpEstimatedTicketSize_modal_minGreaterThanZero(),
        )
        .max(
          maximumAmount ?? Number.MAX_SAFE_INTEGER,
          m.coInvestments_gpEstimatedTicketSize_modal_minNotExceedMax(),
        ),
      currency: yup.string().required(),
    }),
  });

export const EditEstimatedMinTicketSizeModal = ({
  dealOpportunityId,
  dealShortlistItemId,
  dealId,
  dealCurrency,
  isOpen,
  onClose,
  initialValues,
}: EditEstimatedMinTicketSizeModalProps) => {
  const defaultCurrency = useDefaultCurrency();
  const currency = dealCurrency ?? defaultCurrency;
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();

  const mutationOptions = {
    onSuccess: () => {
      showToast({
        variant: "success",
        title: m.coInvestments_gpEstimatedTicketSize_success(),
      });
      queryClient.invalidateQueries({
        queryKey: getGetDealTrackingForGpQueryKey(dealId),
      });
      queryClient.invalidateQueries({
        queryKey: getGetDealShortlistQueryKey(dealId),
      });
      onClose();
    },
    onError: (error: ErrorType<unknown>) => {
      showErrorToast(error, {
        defaultMessage: m.coInvestments_gpEstimatedTicketSize_error(),
      });
    },
  };

  const { mutate: updateGpInternalEstimates, isPending: isPendingGp } =
    useUpdateGpInternalEstimatesForDealOpportunity({
      mutation: mutationOptions,
    });

  const { mutate: updateDealShortlistItem, isPending: isPendingShortlist } =
    useUpdateDealShortlistItem({
      mutation: mutationOptions,
    });

  const isPending = isPendingGp || isPendingShortlist;

  const getInitialValues = (): FormValues => ({
    amount: initialValues?.estimatedTicketSizeMin ?? {
      amount: 0,
      currency,
    },
  });

  const handleSubmit = (values: FormValues) => {
    if (dealShortlistItemId) {
      updateDealShortlistItem({
        dealId,
        dealShortlistItemId,
        data: {
          estimatedTicketSizeMin: toMoneyPayload(values.amount),
          estimatedTicketSizeMax: toMoneyPayload(
            initialValues?.estimatedTicketSizeMax,
          ),
        },
      });
    } else if (dealOpportunityId) {
      updateGpInternalEstimates({
        dealOpportunityId,
        data: {
          estimatedTicketSizeMin: toMoneyPayload(values.amount),
          estimatedTicketSizeMax: toMoneyPayload(
            initialValues?.estimatedTicketSizeMax,
          ),
          gpIndicativeOrder: toMoneyPayload(initialValues?.gpIndicativeOrder),
        },
      });
    }
  };

  return (
    <ActionaryModal
      dataTestId="gp-estimated-min-ticket-size-modal"
      fluidHeight
      open={isOpen}
      setOpen={onClose}
      title={m.coInvestments_gpEstimatedTicketSize_modal_titleMinimum()}
    >
      <Formik
        enableReinitialize
        initialValues={getInitialValues()}
        onSubmit={handleSubmit}
        validationSchema={createValidationSchema(
          initialValues?.estimatedTicketSizeMax?.amount,
        )}
      >
        <Form className="space-y-6">
          <MoneyField
            allowAbbreviations
            currency={currency}
            isRequired
            label={m.coInvestments_gpEstimatedTicketSize_modal_minLabel()}
            name="amount"
          />

          <ModalButtonGroup
            cancelTestId="cancel-gp-estimated-min-ticket-size-button"
            confirmTestId="confirm-gp-estimated-min-ticket-size-button"
            isSubmitting={isPending}
            onCancel={onClose}
          />
        </Form>
      </Formik>
    </ActionaryModal>
  );
};
